import React, { useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { scrollTop } from "src/utils/scrollTop";
import { selectorDeals } from "src/redux/deals/selector";
import { getDealsAsync, ITEMS_PER_PAGE } from "src/redux/deals/actions";
import { DealCard } from "src/page-components/cabinet/deals/DealCard";
import { noDealsBannerTrns } from "src/page-components/cabinet/deals/trns";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { Loader } from "src/components/Loader";
import { AlertBadge } from "src/components/AlertBadge";
import { NoItemsBanner } from "src/components/NoItemsBanner";
import { Pagination } from "src/components/Pagination";
import { SORTING_STATUS_OPTIONS } from "src/components/SortingDropdownList/constants";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { PATHNAMES } from "src/constants/routes";
import { ReactComponent as PersonAddIcon } from "src/assets/icons/person-add.svg";
import { ISelectOption } from "src/@types/form";
import { Sizes } from "src/@types/sizes";
import { Statuses } from "src/@types/deals";
import { MetadataWrapper } from "src/components/MetadataWrapper";

const Deals: FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { getQueryParam, setQueryParam, setMultipleQueryParams } =
    useQueryParams();

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const activeOption = SORTING_STATUS_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_PARAM_KEYS.STATUS)
  );

  const setOption = ({ value }: ISelectOption) =>
    setMultipleQueryParams({
      [QUERY_PARAM_KEYS.STATUS]: value,
      [QUERY_PARAM_KEYS.PAGE]: 1,
    });

  const setPage = (page: number) => {
    scrollTop();

    setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
  };

  const dealsResponse = useAppSelector(selectorDeals);
  const { isLoading, data } = dealsResponse || {};

  useEffect(() => {
    dispatch(
      getDealsAsync({ sorting: activeOption?.value, page: currentPage })
    );
  }, [dispatch, activeOption, currentPage]);

  const dealsResult = data?.results || [];

  const pageCount = Math.ceil(data?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  const countNewRequest = dealsResult.reduce(
    (count, deal) => (deal.status === Statuses.NEW_REQUEST ? count + 1 : count),
    0
  );

  return (
    <MetadataWrapper name="deals">
      {isLoading ? (
        <div className="flex justify-center items-center min-h-72">
          <Loader size={Sizes.L} />
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <SortingDropdownList
              options={SORTING_STATUS_OPTIONS}
              activeOption={activeOption || SORTING_STATUS_OPTIONS[0]}
              setOption={setOption}
              isStatus
            />

            {countNewRequest !== 0 && (
              <AlertBadge icon={PersonAddIcon} count={countNewRequest} />
            )}
          </div>

          <div className="flex flex-col gap-5">
            {dealsResult?.length > 0 ? (
              Boolean(dealsResult?.length) &&
              dealsResult.map((deal) => <DealCard key={deal.id} {...deal} />)
            ) : (
              <NoItemsBanner
                title={t(noDealsBannerTrns.title)}
                description={t(noDealsBannerTrns.description)}
                href={PATHNAMES.CLONES}
                textButton={t(noDealsBannerTrns.addStartDealButton)}
              />
            )}

            {isPaginated && (
              <>
                <Pagination
                  className="self-center my-8"
                  page={currentPage}
                  pageCount={pageCount}
                  setPage={setPage}
                />
              </>
            )}
          </div>
        </div>
      )}
    </MetadataWrapper>
  );
};

export default Deals;
